import type { DriverType } from './engine/types'
import {
  DRIVER_LABELS,
  PICKER_DRIVER_ORDER,
  type PickerDriver,
} from './gridClasses'

const PERCENT_DRIVERS = new Set<PickerDriver>([
  'mom_pct',
  'yoy_pct',
  'pct_gross_sales',
  'pct_gross_sales_growth',
  'pct_of_line',
])

const COUNT_INPUT_DRIVERS = new Set<PickerDriver>([
  'count_value',
  'count_per_calendar_day',
  'count_per_open_day',
  'dio',
  'dso',
  'dpo',
])

const USD_INPUT_DRIVERS = new Set<PickerDriver>([
  'dollar_value',
  'dollar_per_calendar_day',
  'dollar_per_pay_period',
  'dollar_per_open_day',
  'dollar_per_sq_ft',
  'dollar_per_1000_sqft_calendar_day',
  'dollar_per_fte',
  'dollar_per_line_unit',
  'mom_dollar',
  'yoy_dollar',
])

export const DENOM_ZERO_DRIVERS = new Set<PickerDriver>([
  'count_per_open_day',
  'dollar_per_open_day',
  'dollar_per_sq_ft',
  'dollar_per_1000_sqft_calendar_day',
  'dollar_per_fte',
])

export function orderedPickerDrivers(allowed: DriverType[]): PickerDriver[] {
  const allowedSet = new Set(allowed)
  return PICKER_DRIVER_ORDER.filter((driver) => allowedSet.has(driver))
}

export function driverInputUnit(type: PickerDriver): 'usd' | 'count' | 'pct' {
  if (COUNT_INPUT_DRIVERS.has(type)) return 'count'
  if (USD_INPUT_DRIVERS.has(type)) return 'usd'
  if (PERCENT_DRIVERS.has(type)) return 'pct'
  return 'usd'
}

function valueUnitForDriverMatch(
  valueUnit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct',
): 'usd' | 'count' | 'pct' {
  return valueUnit === 'usd_full' || valueUnit === 'usd_salary_k' ? 'usd' : valueUnit
}

export function carryForwardDisplayDriver(
  valueUnit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct',
): 'count_value' | 'dollar_value' {
  return valueUnitForDriverMatch(valueUnit) === 'count' ? 'count_value' : 'dollar_value'
}

export function isPercentDriver(type: PickerDriver): boolean {
  return PERCENT_DRIVERS.has(type)
}

export function formatDriverInput(type: PickerDriver, value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return ''
  }
  if (isPercentDriver(type)) {
    const pct = value * 100
    return String(Number(pct.toFixed(4)))
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 8 })
}

export function parseDriverInput(type: PickerDriver, raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, '')
  if (!trimmed || trimmed === '-') {
    return null
  }
  const parenthesized = trimmed.startsWith('(') && trimmed.endsWith(')')
  const num = Number(trimmed.replace(/[()%]/g, ''))
  if (!Number.isFinite(num)) {
    return null
  }
  const signed = parenthesized ? -Math.abs(num) : num
  if (isPercentDriver(type)) {
    return signed / 100
  }
  return signed
}

export function isIdentity(
  driverType: PickerDriver,
  driverUnit: 'usd' | 'count' | 'pct',
  valueUnit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct',
  driverInput: number | null,
  computed: number | null,
  options: { hasOneTime?: boolean; denomZero?: boolean } = {},
): boolean {
  if (options.hasOneTime || options.denomZero) {
    return false
  }
  if (driverType !== 'count_value' && driverType !== 'dollar_value') {
    return false
  }
  if (driverUnit !== valueUnitForDriverMatch(valueUnit)) {
    return false
  }
  if (driverInput == null || computed == null) {
    return false
  }
  return Math.abs(driverInput - computed) < 0.005
}

export function isDenomZero(
  driverType: PickerDriver | null,
  driverInput: number | null,
  computed: number | null,
): boolean {
  if (!driverType || driverInput == null) {
    return false
  }
  if (!DENOM_ZERO_DRIVERS.has(driverType)) {
    return false
  }
  return computed == null
}

export function driverHelpText(type: PickerDriver | null): string {
  if (!type) {
    return 'Choose how projected values are computed from your inputs.'
  }
  switch (type) {
    case 'count_value':
      return 'The count you enter is the projected value for that month.'
    case 'dollar_value':
      return 'The dollar amount you enter is the projected value for that month.'
    case 'count_per_calendar_day':
      return 'Count per calendar day × days in that month'
    case 'dollar_per_calendar_day':
      return '$ per calendar day × days in that month'
    case 'dollar_per_pay_period':
      return '$ per pay period × pay dates in that month'
    case 'labor_buildup':
      return 'Labor build-up: FTE × rate × 80 h + salaries, per pay date'
    case 'count_per_open_day':
      return 'Count per open day × Days Open for Business that month'
    case 'dollar_per_open_day':
      return '$ per open day × Days Open for Business that month'
    case 'dollar_per_sq_ft':
      return '$ per sq ft × Square Footage that month'
    case 'dollar_per_1000_sqft_calendar_day':
      return '$ per 1,000 sq ft × Square Footage/1000 × days in that month'
    case 'dollar_per_fte':
      return '$ per FTE × (BOH + FOH) that month'
    case 'mom_pct':
      return "Prior month's value × (1 + input %)"
    case 'mom_dollar':
      return "Prior month's value + the $ entered"
    case 'yoy_pct':
      return 'Same month last year × (1 + input %)'
    case 'yoy_dollar':
      return 'Same month last year + the $ entered'
    case 'pct_gross_sales':
      return 'Input % × Gross Product Sales that month'
    case 'pct_gross_sales_growth':
      return "Prior month's value × (1 + input % × Gross Product Sales month-over-month growth)"
    case 'pct_of_line':
      return 'Input % × the reference line that month'
    case 'dollar_per_line_unit':
      return '$ per unit of the reference line × that line that month'
    case 's_curve':
      return 'Logistic curve from start to steady state across 36 months'
    case 'dio':
      return 'DIO / days in month × consolidated Total COGS'
    case 'dso':
      return 'DSO / days in month × consolidated Net Revenue'
    case 'dpo':
      return 'DPO / days in month × consolidated Total COGS'
    case 'carry_forward':
      return 'Holds the last actual (M+0) value constant for every projected month.'
    case 'ignore':
      return 'Ignores child lines and lets you drive the subtotal directly.'
    default:
      return DRIVER_LABELS[type]
  }
}

export function denomZeroHelpText(type: PickerDriver): string {
  switch (type) {
    case 'count_per_open_day':
    case 'dollar_per_open_day':
      return 'No open days in this month, so a per-open-day driver has nothing to multiply. Set a different driver or add open days.'
    case 'dollar_per_sq_ft':
    case 'dollar_per_1000_sqft_calendar_day':
      return 'Square footage is zero this month, so this driver has nothing to multiply. Set a different driver or enter square footage.'
    case 'dollar_per_fte':
      return 'BOH + FOH (and salaried headcount at G&A) is zero this month, so a per-FTE driver has nothing to multiply. Set a different driver or enter FTE/headcount.'
    default:
      return 'The denominator is zero this month, so this driver cannot compute a value.'
  }
}
